import React, { useEffect, useRef } from 'react';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import { SuccessIcon, CheckboxIcon, CheckboxOutlineIcon } from '../../utils/icons';

const DropdownMenu = ({
  isOpen = false,
  onClose,
  options = [],
  selectedValue = null,
  selectedValues = [],
  onSelect,
  onMultiSelect,
  isMultiSelect = false,
  position = "bottom-right",
  width = 180,
  showCheck = true,
  anchorRef,
  className = ""
}) => {
  const dropdownRef = useRef(null);
  
  const selectedValuesArray = isMultiSelect ? selectedValues : (selectedValue ? [selectedValue] : []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          anchorRef?.current && !anchorRef.current.contains(event.target)) {
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose, anchorRef]);

  const getDropdownPosition = () => {
    const positions = {
      "bottom-left": "top-full left-0 mt-1",
      "bottom-right": "top-full right-0 mt-1", 
      "top-left": "bottom-full left-0 mb-1",
      "top-right": "bottom-full right-0 mb-1"
    };
    return positions[position] || positions["bottom-right"];
  };

  const handleOptionSelect = (optionValue) => {
    if (isMultiSelect) {
      if (onMultiSelect) {
        const newValues = selectedValuesArray.includes(optionValue)
          ? selectedValuesArray.filter(v => v !== optionValue)
          : [...selectedValuesArray, optionValue];
        onMultiSelect(newValues);
      }
      // Don't close dropdown for multi-select
    } else {
      if (onSelect) {
        onSelect(optionValue);
      }
      onClose?.();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />
      
      {/* Options */}
      <div 
        ref={dropdownRef}
        className={`absolute z-50 ${getDropdownPosition()} rounded-lg overflow-hidden ${className}`}
        style={{ 
          backgroundColor: colors.bg.tertiary,
          boxShadow: "0px 1px 16px 0 rgba(0,0,0,0.4)",
          width: typeof width === "number" ? `${width}px` : width
        }}
      >
        <div className="flex flex-col max-h-[350px] overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/20">
          {options.map((option, index) => {
            const isSelected = isMultiSelect 
              ? selectedValuesArray.includes(option.value)
              : option.value === selectedValue;
            const isDisabled = option.disabled;
            
            const hasDescription = option.description && option.description.trim();
            const dynamicHeight = hasDescription ? 'min-h-[60px] py-3' : 'h-10 py-[13px]';
            
            return (
              <div
                key={option.value || index}
                className={`flex items-center flex-shrink-0 ${dynamicHeight} px-3 gap-2 transition-colors overflow-hidden ${
                  isDisabled
                    ? 'opacity-50 cursor-not-allowed'
                    : isSelected
                    ? 'hover:bg-white/[0.06] cursor-pointer'
                    : 'hover:bg-white/[0.02] cursor-pointer'
                }`}
                style={{
                  backgroundColor: isSelected && !isDisabled 
                    ? colors.bg.fog_thin
                    : colors.bg.tertiary
                }}
                onClick={() => !isDisabled && handleOptionSelect(option.value)}
              >
                {/* Option Icon */}
                {option.icon && (
                  <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center" style={{color: colors.text.primary}}>
                    {option.icon}
                  </div>
                )}
                
                {/* Option Content */}
                <div className="flex flex-col flex-grow gap-0.5">
                  {/* Option Label */}
                  <p className="text-left" style={{ ...typography.small.title, color: colors.text.primary || '#FAF7F5' }}>
                    {option.label}
                  </p>
                  
                  {/* Option Description */}
                  {hasDescription && (
                    <p className="text-left whitespace-pre-line" style={{ ...typography.xsmall.regular, color: colors.text.secondary }}>
                      {option.description}
                    </p>
                  )}
                </div>
                
                {/* Multi-select Checkbox or Single-select Check Icon */}
                {isMultiSelect ? (
                  <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center" style={{color: colors.text.primary}}>
                    {isSelected ? <CheckboxIcon /> : <CheckboxOutlineIcon />}
                  </div>
                ) : showCheck && isSelected && !isDisabled && (
                  <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center" style={{color: colors.text.primary}}>
                    <SuccessIcon />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default DropdownMenu;